/**
 * Ví và gói thuê bao CỦA PHÒNG KHÁM trả cho Addy (#18974).
 * Real /api/v1/clinic/billing.
 *
 * Tên file là `saasBilling`, không phải `billing`, và khoảng cách đó là có chủ ý: `billing.ts`
 * bên cạnh là hoá đơn VIỆN PHÍ của bệnh nhân. Hai loại tiền, hai ví, hai người duyệt — và gộp
 * tên sẽ dẫn tới đúng loại nhầm lẫn mà cả Epic này dựng ranh giới để tránh.
 */
import { apiClient } from "./client";

export type BillingState = "ACTIVE" | "GRACE" | "READ_ONLY";

export interface SaasBillingSummary {
  planCode: string | null;
  activeFrom: string | null;
  activeTo: string | null;
  billingState: BillingState;
  graceUntil: string | null;
  balanceVnd: number;
  /** Tiền đã bắt đầu trả nhưng CHƯA thành số dư — webhook chưa về, hoặc khách bỏ dở. */
  pendingTopUpVnd: number;
  /** Sổ cái và số dư có khớp không. Lệch nghĩa là con số phía trên không đáng tin. */
  reconciled: boolean;
  /**
   * Số tiền nạp tối thiểu, do MÁY CHỦ quyết (khác nhau giữa dev và thật).
   *
   * Giao diện đọc con số này thay vì tự giữ một hằng số riêng — hai bản sao của cùng một quy tắc
   * là hai thứ sẽ lệch nhau, và lần lệch đầu tiên đã làm nút "Nạp tiền" im lặng không phản hồi.
   */
  minTopUpVnd: number;
}

export type WalletTransactionType =
  | "TOP_UP"
  | "SUBSCRIPTION_CHARGE"
  | "SERVICE_CHARGE"
  | "REFUND_TO_WALLET"
  /** Addy bao phí cổng theo cam kết của gói — KHÔNG phải hoàn lại một khoản đã thu. */
  | "PAYMENT_FEE_REBATE"
  | "REFUND_TO_BANK"
  | "MANUAL_ADJUSTMENT";

export interface WalletEntry {
  id: string;
  type: WalletTransactionType;
  /** Dương là cộng, âm là trừ. Dấu nằm trong số chứ không nằm trong loại. */
  amountVnd: number;
  balanceAfterVnd: number;
  sourceRef: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface SubscriptionPeriod {
  id: string;
  planId: string;
  /** Giá CHỐT lúc mua. Bảng giá đổi về sau không làm đổi con số này. */
  priceVnd: number;
  startsAt: string;
  endsAt: string;
  source: "TEST" | "WALLET" | "DIRECT";
  walletTransactionId: string | null;
  createdAt: string;
}

export type SaasPaymentStatus =
  | "PENDING"
  | "PAID"
  | "CANCELLED"
  | "FAILED"
  | "EXPIRED"
  | "RECEIVED_UNAPPLIED";

export interface SaasPaymentIntent {
  id: string;
  purpose: "TOP_UP" | "SUBSCRIPTION";
  amountVnd: number;
  provider: string;
  providerOrderCode: number;
  checkoutUrl: string | null;
  status: SaasPaymentStatus;
  expiresAt: string | null;
  createdAt: string;
}

const BASE = "/v1/clinic/billing";

export const saasBillingApi = {
  summary(): Promise<SaasBillingSummary> {
    return apiClient.get<SaasBillingSummary>(`${BASE}/summary`).then((r) => r.data);
  },
  transactions(): Promise<WalletEntry[]> {
    return apiClient.get<WalletEntry[]>(`${BASE}/wallet/transactions`).then((r) => r.data);
  },
  periods(): Promise<SubscriptionPeriod[]> {
    return apiClient.get<SubscriptionPeriod[]>(`${BASE}/periods`).then((r) => r.data);
  },
  payments(): Promise<SaasPaymentIntent[]> {
    return apiClient.get<SaasPaymentIntent[]>(`${BASE}/payments`).then((r) => r.data);
  },
  /**
   * Tạo lệnh nạp tiền. Trả về đơn kèm checkoutUrl để mở PayOS.
   *
   * Trang KHÔNG dựa vào việc khách quay lại: nếu họ đóng trình duyệt giữa chừng, đơn vẫn ở
   * PENDING và webhook vẫn cộng tiền khi tới.
   */
  /**
   * Huỷ một lệnh đang chờ.
   *
   * Chỉ bỏ nó khỏi danh sách chờ — KHÔNG ngăn được tiền chuyển đến. Mã QR là một lệnh chuyển
   * khoản; huỷ ở đây không nói gì với ngân hàng. Đã chuyển rồi thì tiền vẫn vào ví.
   */
  cancel(id: string): Promise<SaasPaymentIntent> {
    return apiClient.post<SaasPaymentIntent>(`${BASE}/payments/${id}/cancel`).then((r) => r.data);
  },
  topUp(amountVnd: number): Promise<SaasPaymentIntent> {
    return apiClient.post<SaasPaymentIntent>(`${BASE}/top-up`, { amountVnd }).then((r) => r.data);
  },
};

export const saasBillingQueryKeys = {
  summary: () => ["saas-billing", "summary"] as const,
  transactions: () => ["saas-billing", "transactions"] as const,
  periods: () => ["saas-billing", "periods"] as const,
  payments: () => ["saas-billing", "payments"] as const,
};
